import AbstractItem from "../AbstractItem.js";

/**
 * Abstract report row.
 *
 * Rows are composite. Their direct children can be accessed through
 * `getRows()`, and all children with `getAllRows()`.
 *
 * @abstract
 * @param {item} [spec.parent] - Parent of the row.
 * @param {string} [spec.masterVisibilityRowTemplateId] - Template id of the row whose visibility is the same as `that`.
 */
export default class AbstractRow extends AbstractItem {
	constructor({ parent, masterVisibilityRowTemplateId } = {}) {
		super(...arguments);
		this._parent = parent || this._report;
		this._masterVisibilityRowTemplateId = masterVisibilityRowTemplateId;
		this._masterVisibilityRow = null;
	}

	setReport(report) {
		super.setReport(report);

		// Top-level rows have the report as their parent.
		this._parent ||= this._report;
	}

	getStyle() {
		return "";
	}

	getMasterVisibilityRow() {
		if (!this._masterVisibilityRowTemplateId) {
			return null;
		}

		this._masterVisibilityRow ||= this._report
			.getAllRows()
			.find(
				(row) =>
					row.getTemplate().getId() ===
					this._masterVisibilityRowTemplateId,
			);

		return this._masterVisibilityRow;
	}

	getRows() {
		return [];
	}

	getVisibleRows() {
		return this.getRows().filter((row) => row.isVisible());
	}

	/**
	 * Return all sub-rows fetched recursively.
	 *
	 * The result is computed only once and cached afterwards. Because
	 * reports are read-only, there is no need to compute the list more than
	 * once.
	 *
	 * @return {abstractRow[]} - Flatten array of rows.
	 */
	getAllRows() {
		this._cachedAllRows ||= this._getAllRows();
		return this._cachedAllRows;
	}

	flushAllRowsCache() {
		this._cachedAllRows = undefined;
	}

	isGenerated() {
		return false;
	}

	hasDataOrHasChildWithData() {
		return false;
	}

	/**
	 * @return {function} - Formatter for data of the row.
	 *
	 * The default is to delegate all formatting to the default (report)
	 * formatter.
	 */
	getFormatter({ defaultFormatter }) {
		return defaultFormatter;
	}

	/**
	 * @return {boolean} - Whether the row has sub-rows.
	 */
	hasRows() {
		return this.getRows().length !== 0;
	}

	/**
	 * @return {boolean} - Whether the row can have sub-rows.
	 */
	canHaveChildren() {
		return false;
	}

	canBeReferenced() {
		return super.canBeReferenced() && !this.isClone();
	}

	isLeaf() {
		return !this.canHaveChildren();
	}

	canBeCollapsed() {
		return !this.isClone();
	}

	canBeSelected() {
		return !this.isClone();
	}

	isGroupRow() {
		return false;
	}

	isAccountRow() {
		return false;
	}

	isAccountGroupRow() {
		return false;
	}

	isGeneratedAccountGroupRow() {
		return false;
	}

	isFormulaRow() {
		return false;
	}

	isClone() {
		return this.getParent() && this.getParent().isCloneRow(this);
	}

	isCloneRow() {
		return false;
	}

	isNegated() {
		return false;
	}

	isForAccountNumber(_accountNumber) {
		return false;
	}

	getDepth() {
		return this._parent.getDepth() + 1;
	}

	getVisibilityDepth() {
		return this._firstVisibleParent().getDepth() + 1;
	}

	/**
	 * Return true if the receiver has data for any column in the report
	 */
	hasData() {
		return false;
	}

	isRow() {
		return true;
	}

	isCollapsed() {
		return false;
	}

	isExpanded() {
		return !this.isCollapsed();
	}

	accept(visitor) {
		return visitor.visitAbstractRow(this);
	}

	compareTo(anotherRow) {
		// the order doesn't matter, but the return value must be
		// consistent with other calls to `compareTo()`:
		return this.getType() < anotherRow.getType();
	}

	expandAccordingTo(_anotherRow) {}

	_getAllRows() {
		const rows = [];

		this.getRows().forEach((row) => {
			// eslint-disable-next-line no-shadow -- bad practice, we should fix this
			row.getAllRows().forEach((row) => {
				rows.push(row);
			});
		});

		return rows.concat(this.getRows());
	}

	_firstVisibleParent() {
		return (
			this._getAllParents().find((parent) => parent.isCollapsed()) ||
			this._parent
		);
	}

	_getAllParents() {
		let nextParent = this._parent;
		const allParents = [];
		while (nextParent.isRow()) {
			allParents.push(nextParent);
			nextParent = nextParent.getParent();
		}

		return allParents.reverse();
	}

	/**
	 * Only send properties needed by the server for data calculation.
	 */
	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.rowType = this.getType();
		properties.children = this.getRows();

		properties.masterVisibilityRowTemplateId =
			this._masterVisibilityRowTemplateId;

		return properties;
	}
}
